/**
 * PdfFlipViewer — full-screen flipbook viewer for single-document PDFs.
 * Used for the Pre-Design Research Report, legacy spec/schedule PDFs, and any future
 * document that should open as an interactive flipbook.
 *
 * Props: { pdfUrl, title?, onClose, landscape? }
 * - Portrait (default): two-page spread, white backing page + content page, corner peel.
 * - Landscape: single-page display sized to fill the screen, same corner-peel nav.
 *
 * Magnifier: off by default; toggle button in the header enables hover-glass + click-to-pan.
 */
import { useState, useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import HTMLFlipBook from 'react-pageflip'
import { loadPdfjs } from '../lib/pdfjs.js'

const RATIO      = 8.5 / 11   // portrait page aspect (w/h)
const LENS       = 300         // magnifier lens diameter (px)
const LENS_ZOOM  = 2.4         // hover-lens magnification
const FULL_ZOOM  = 2.6         // click-to-expand pan view magnification

// Full-cover overlay with all four corners cut out confines drag-to-peel to those corners.
function cornerTri(h, w) { return Math.round(Math.min(108, h * 0.26, w * 0.26)) }
const cornerClip = t => `polygon(${t}px 0px, calc(100% - ${t}px) 0px, 100% ${t}px, 100% calc(100% - ${t}px), calc(100% - ${t}px) 100%, ${t}px 100%, 0px calc(100% - ${t}px), 0px ${t}px)`

async function rasterizeAll(url, targetW) {
  try {
    const pdfjs = await loadPdfjs()
    if (!pdfjs) return null
    const pdf = await pdfjs.getDocument({ url }).promise
    const out = []
    for (let n = 1; n <= pdf.numPages; n++) {
      const page = await pdf.getPage(n)
      const base = page.getViewport({ scale: 1 })
      const scale = Math.max(0.4, Math.min(6, targetW / base.width))
      const vp = page.getViewport({ scale })
      const canvas = document.createElement('canvas')
      canvas.width = Math.ceil(vp.width); canvas.height = Math.ceil(vp.height)
      const cctx = canvas.getContext('2d')
      cctx.fillStyle = '#fff'; cctx.fillRect(0, 0, canvas.width, canvas.height)
      await page.render({ canvasContext: cctx, viewport: vp }).promise
      out.push({ url: canvas.toDataURL('image/jpeg', 0.85), w: canvas.width, h: canvas.height })
    }
    try { pdf.destroy() } catch { /* noop */ }
    return out
  } catch { return null }
}

export default function PdfFlipViewer({ pdfUrl, title, onClose, landscape = false }) {
  const [pagesImg, setPagesImg]   = useState(undefined)  // [{url,w,h}…] | 'fail' | undefined
  const [cur, setCur]             = useState(0)
  const [vp, setVp]               = useState(() => ({ w: window.innerWidth, h: window.innerHeight }))
  const [magOn, setMagOn]         = useState(false)
  const [lens, setLens]           = useState(null)
  const [full, setFull]           = useState(null)
  const [flipping, setFlipping]   = useState(false)
  const bookRef = useRef(null)
  const wrapRef = useRef(null)

  // Mobile: single-page with full-page swipe + big tap targets.
  // Landscape (11×17): still a two-page spread with gray backing on left — same book behavior as portrait.
  const isMobile   = vp.w <= 640
  const singlePage = isMobile

  const flipNext = () => bookRef.current?.pageFlip?.()?.flipNext?.()
  const flipPrev = () => bookRef.current?.pageFlip?.()?.flipPrev?.()

  // Landscape pages are wider — rasterize at higher resolution so table text stays crisp.
  useEffect(() => {
    let alive = true
    if (!pdfUrl) { setPagesImg('fail'); return }
    rasterizeAll(pdfUrl, landscape ? 1700 : 1100).then(d => { if (alive) setPagesImg(d && d.length ? d : 'fail') })
    return () => { alive = false }
  }, [pdfUrl, landscape])

  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight })
    window.addEventListener('resize', onResize)
    window.addEventListener('orientationchange', onResize)
    return () => { window.removeEventListener('resize', onResize); window.removeEventListener('orientationchange', onResize) }
  }, [])

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') { e.stopImmediatePropagation(); if (full) { setFull(null) } else { onClose() } }
      else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.stopImmediatePropagation(); e.preventDefault()
        if (full) return
        if (e.key === 'ArrowRight') flipNext()
        else flipPrev()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose, full]) // eslint-disable-line react-hooks/exhaustive-deps

  // Turn off magnifier state when mag is toggled off.
  function toggleMag() {
    setMagOn(o => !o)
    setLens(null)
    setFull(null)
  }

  // Page size. Portrait desktop = two pages side by side. Single-page modes fill available space.
  const dims = useMemo(() => {
    const ratio = landscape ? (17 / 11) : RATIO
    if (singlePage) {
      const pad = isMobile ? 20 : 130
      const availW = vp.w - pad
      const availH = vp.h - 124
      let w = availW, h = w / ratio
      if (h > availH) { h = availH; w = h * ratio }
      return { w: Math.max(300, Math.round(w)), h: Math.max(180, Math.round(h)) }
    }
    const availH = vp.h - 150
    const availW = vp.w - 130
    let h = availH, w = h * ratio
    if (w * 2 > availW) { w = availW / 2; h = w / ratio }
    return { w: Math.max(220, Math.round(w)), h: Math.max(280, Math.round(h)) }
  }, [vp, isMobile, singlePage, landscape]) // eslint-disable-line react-hooks/exhaustive-deps

  const ready  = Array.isArray(pagesImg)
  const total  = ready ? pagesImg.length : 0

  // Portrait desktop: interleave gray backing pages so every spread reads white-left + page-right.
  // Single-page modes: just the content pages.
  const pages = useMemo(() => {
    if (!ready) return []
    const out = []
    pagesImg.forEach((img, i) => {
      if (!singlePage) {
        out.push(
          <div key={'b' + i} style={St.page}>
            <div style={St.backing} />
            <div style={St.gutterRight} />
          </div>
        )
      }
      out.push(
        <div key={'p' + i} style={St.page}>
          <img src={img.url} alt={'Page ' + (i + 1)} draggable={false} style={St.pageImg} />
          {!singlePage && <div style={St.gutterLeft} />}
        </div>
      )
    })
    return out
  }, [ready, pagesImg, singlePage]) // eslint-disable-line react-hooks/exhaustive-deps

  // Image area relative to the wrap container.
  // Portrait two-page: content is always on the right half. Single-page: full container.
  function imgArea() {
    if (singlePage) return { x: 0, y: 0, w: dims.w, h: dims.h }
    return { x: dims.w, y: 0, w: dims.w, h: dims.h }
  }

  function onLensMove(e) {
    if (flipping || full || !magOn) { setLens(null); return }
    if (!wrapRef.current) return
    const wr  = wrapRef.current.getBoundingClientRect()
    const ia  = imgArea()
    const ix  = (e.clientX - wr.left) - ia.x
    const iy  = (e.clientY - wr.top)  - ia.y
    if (ix < 0 || iy < 0 || ix > ia.w || iy > ia.h) { setLens(null); return }
    const src = pagesImg?.[cur]?.url
    if (!src) { setLens(null); return }
    setLens({
      sx: e.clientX, sy: e.clientY, src,
      bgW: ia.w * LENS_ZOOM, bgH: ia.h * LENS_ZOOM,
      bgX: -(ix * LENS_ZOOM - LENS / 2), bgY: -(iy * LENS_ZOOM - LENS / 2),
    })
  }

  function onLensClick(e) {
    if (flipping || !magOn) return
    if (!wrapRef.current) return
    const wr  = wrapRef.current.getBoundingClientRect()
    const ia  = imgArea()
    const ix  = (e.clientX - wr.left) - ia.x
    const iy  = (e.clientY - wr.top)  - ia.y
    if (ix < 0 || iy < 0 || ix > ia.w || iy > ia.h) return
    setLens(null)
    setFull({ fx: ix / ia.w, fy: iy / ia.h, ar: ia.w / ia.h, src: pagesImg?.[cur]?.url })
  }

  const t    = cornerTri(dims.h, dims.w)
  const bookW = singlePage ? dims.w : dims.w * 2

  const hintText = isMobile
    ? 'Swipe, or tap ‹ › to turn pages'
    : magOn
      ? 'Hover to magnify · click to zoom & pan · turn at the right corners or use ‹ ›'
      : 'Turn at the right corners, use ‹ ›, or the arrow keys'

  return createPortal(
    <div style={St.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <style>{`.stf__item, .stf__block { background:#fff; }`}</style>

      <div style={St.header}>
        <div style={{ minWidth: 0 }}>
          <div style={St.hTitle}>{title || 'Document'}</div>
          {ready && <div style={St.hMeta}>Page {Math.min(cur, total - 1) + 1} of {total}</div>}
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
          {!isMobile && (
            <button onClick={toggleMag} style={{ ...St.hLink, background: magOn ? 'rgba(255,255,255,0.15)' : 'transparent', cursor: 'pointer' }}
              title={magOn ? 'Magnifier on — click to disable' : 'Enable magnifier'}>
              Magnify{magOn ? ' ✓' : ''}
            </button>
          )}
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" download style={St.hLink}>Download PDF ↓</a>
          <button onClick={onClose} style={St.hClose} title="Close (Esc)">✕</button>
        </div>
      </div>

      <div style={St.stage}>
        {pagesImg === undefined && <div style={St.msg}>Rendering…</div>}
        {pagesImg === 'fail'    && <div style={St.msg}>Could not load the document.</div>}
        {ready && (
          <>
            {!isMobile && <button style={St.nav} onClick={flipPrev} title="Previous (←)">‹</button>}
            <div ref={wrapRef} style={{ position: 'relative', width: bookW, height: dims.h }}>
              <HTMLFlipBook
                ref={bookRef}
                width={dims.w}
                height={dims.h}
                size="fixed"
                usePortrait={singlePage}
                showCover={false}
                showPageCorners={isMobile}
                useMouseEvents={true}
                mobileScrollSupport={false}
                swipeDistance={isMobile ? 12 : 30}
                drawShadow
                maxShadowOpacity={0.5}
                flippingTime={isMobile ? 550 : 650}
                onFlip={e => setCur(singlePage ? e.data : Math.floor(e.data / 2))}
                onChangeState={e => setFlipping(e.data !== 'read')}
              >
                {pages}
              </HTMLFlipBook>

              {/* Desktop: full-cover overlay with four corners cut out to confine the native peel.
                  When magnifier is on the overlay also tracks the lens and handles click-to-zoom. */}
              {!isMobile && (
                <>
                  <div
                    style={{ position: 'absolute', inset: 0, zIndex: 5, clipPath: cornerClip(t), pointerEvents: 'auto',
                      cursor: magOn ? (lens ? 'none' : 'zoom-in') : 'default' }}
                    onMouseDown={e => e.stopPropagation()}
                    onMouseMove={magOn && !flipping ? onLensMove : undefined}
                    onMouseLeave={magOn ? () => setLens(null) : undefined}
                    onClick={magOn && !flipping ? onLensClick : undefined}
                  />
                  {/* Subtle dog-ear corner hints */}
                  <div style={{ position: 'absolute', right: 0, top: 0, width: t, height: t, zIndex: 6, pointerEvents: 'none',
                    clipPath: 'polygon(0 0, 100% 0, 100% 100%)', background: 'linear-gradient(225deg, rgba(7,39,45,0.20), rgba(7,39,45,0) 60%)' }} />
                  <div style={{ position: 'absolute', right: 0, bottom: 0, width: t, height: t, zIndex: 6, pointerEvents: 'none',
                    clipPath: 'polygon(100% 0, 100% 100%, 0 100%)', background: 'linear-gradient(315deg, rgba(7,39,45,0.20), rgba(7,39,45,0) 60%)' }} />
                  <div style={{ position: 'absolute', left: 0, top: 0, width: t, height: t, zIndex: 6, pointerEvents: 'none',
                    clipPath: 'polygon(0 0, 100% 0, 0 100%)', background: 'linear-gradient(135deg, rgba(7,39,45,0.20), rgba(7,39,45,0) 60%)' }} />
                  <div style={{ position: 'absolute', left: 0, bottom: 0, width: t, height: t, zIndex: 6, pointerEvents: 'none',
                    clipPath: 'polygon(0 0, 0 100%, 100% 100%)', background: 'linear-gradient(45deg, rgba(7,39,45,0.20), rgba(7,39,45,0) 60%)' }} />
                </>
              )}
            </div>
            {!isMobile && <button style={St.nav} onClick={flipNext} title="Next (→)">›</button>}
          </>
        )}

        {/* Mobile: big floating tap targets at the bottom corners (thumb-reachable). */}
        {ready && isMobile && (
          <>
            <button style={{ ...St.nav, ...St.navMobile, left: 14, opacity: cur <= 0 ? 0.3 : 1 }}
              onClick={flipPrev} disabled={cur <= 0} title="Previous">‹</button>
            <button style={{ ...St.nav, ...St.navMobile, right: 14, opacity: cur >= total - 1 ? 0.3 : 1 }}
              onClick={flipNext} disabled={cur >= total - 1} title="Next">›</button>
          </>
        )}
      </div>

      {ready && <div style={St.hint}>{hintText}</div>}

      {/* Floating magnifier glass — follows the cursor, pure visual, never blocks pointer events. */}
      {lens && (
        <div style={{
          position: 'fixed', left: lens.sx - LENS / 2, top: lens.sy - LENS / 2,
          width: LENS, height: LENS, borderRadius: '50%',
          pointerEvents: 'none', zIndex: 9600, overflow: 'hidden',
          boxShadow: '0 6px 22px rgba(0,0,0,0.45), 0 0 0 2px rgba(255,255,255,0.9) inset',
          border: '3px solid rgba(255,255,255,0.95)', background: '#fff',
          backgroundImage: `url(${lens.src})`, backgroundRepeat: 'no-repeat',
          backgroundSize: `${lens.bgW}px ${lens.bgH}px`,
          backgroundPosition: `${lens.bgX}px ${lens.bgY}px`,
        }} />
      )}

      {/* Click-to-expand full pannable zoom view. */}
      {full && <FullZoom src={full.src} ar={full.ar} start={full} onClose={() => setFull(null)} />}
    </div>,
    document.body
  )
}

/* Full-screen pannable zoom: the image is shown larger than the viewport; the mouse pans it.
   Starts centred on the clicked point; click anywhere to return to the booklet. */
function FullZoom({ src, ar, start, onClose }) {
  const [vp, setVp]   = useState({ w: window.innerWidth, h: window.innerHeight })
  const [frac, setFrac] = useState({ x: start.fx, y: start.fy })
  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight })
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  const fitW  = Math.min(vp.w, vp.h * ar)
  const dispW = fitW * FULL_ZOOM
  const dispH = dispW / ar
  const overX = Math.max(0, dispW - vp.w)
  const overY = Math.max(0, dispH - vp.h)
  const tx = overX > 0 ? -frac.x * overX : (vp.w - dispW) / 2
  const ty = overY > 0 ? -frac.y * overY : (vp.h - dispH) / 2
  return (
    <div
      onMouseMove={e => setFrac({ x: Math.max(0, Math.min(1, e.clientX / vp.w)), y: Math.max(0, Math.min(1, e.clientY / vp.h)) })}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 9700, background: 'rgba(7,39,45,0.96)', overflow: 'hidden', cursor: 'zoom-out' }}
    >
      <img src={src} alt="Zoom" draggable={false}
        style={{ position: 'absolute', left: 0, top: 0, width: dispW, height: dispH, transform: `translate(${tx}px, ${ty}px)`, willChange: 'transform' }} />
      <div style={{ position: 'fixed', bottom: 18, left: 0, right: 0, textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, pointerEvents: 'none' }}>
        Move the mouse to pan · click to return
      </div>
    </div>
  )
}

const St = {
  overlay:   { position: 'fixed', inset: 0, zIndex: 9500, background: 'rgba(7,39,45,0.96)', display: 'flex', flexDirection: 'column' },
  header:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 20px', color: '#fff', flexShrink: 0 },
  hTitle:    { fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  hMeta:     { fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 3 },
  hLink:     { fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: '#fff', textDecoration: 'none', border: '0.5px solid rgba(255,255,255,0.4)', borderRadius: 6, padding: '6px 12px' },
  hClose:    { background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', lineHeight: 1 },
  stage:     { position: 'relative', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, minHeight: 0, padding: '0 10px 10px' },
  nav:       { background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', fontSize: 30, lineHeight: 1, width: 44, height: 64, borderRadius: 8, cursor: 'pointer', flexShrink: 0 },
  navMobile: { position: 'absolute', bottom: 14, zIndex: 10, width: 56, height: 56, borderRadius: '50%', fontSize: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.16)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' },
  page:      { position: 'relative', width: '100%', height: '100%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  backing:   { width: '100%', height: '100%', background: '#eef1f2' },
  pageImg:   { width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#fff' },
  gutterLeft:  { position: 'absolute', left: 0, top: 0, bottom: 0, width: '11%', pointerEvents: 'none', background: 'linear-gradient(to right, rgba(7,39,45,0.16), rgba(7,39,45,0.04) 45%, rgba(7,39,45,0) 100%)' },
  gutterRight: { position: 'absolute', right: 0, top: 0, bottom: 0, width: '11%', pointerEvents: 'none', background: 'linear-gradient(to left, rgba(7,39,45,0.16), rgba(7,39,45,0.04) 45%, rgba(7,39,45,0) 100%)' },
  msg:       { color: 'rgba(255,255,255,0.8)', fontFamily: '"DM Sans",sans-serif', fontSize: 14 },
  hint:      { textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, padding: '0 0 14px' },
}
